/**
 * QuickActionsMenu Component
 *
 * Dropdown menu for quick actions in the TopBar:
 * - New Flow
 * - Run Flow (opens Command Palette)
 * - New Schedule
 * - View Devices
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  FileText,
  Play,
  Calendar,
  Monitor,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';

interface QuickActionsMenuProps {
  /** Callback to open the Command Palette (for Run Flow action) */
  onOpenCommandPalette?: () => void;
}

export function QuickActionsMenu({ onOpenCommandPalette }: QuickActionsMenuProps) {
  const navigate = useNavigate();
  const { isSystemAdmin } = useAuth();

  const handleNewFlow = () => {
    navigate('/flows?action=new');
  };

  const handleRunFlow = () => {
    if (onOpenCommandPalette) {
      onOpenCommandPalette();
    } else {
      // Fallback: navigate to flows page
      navigate('/flows');
    }
  };

  const handleNewSchedule = () => {
    navigate('/tests?action=new');
  };

  const handleViewDevices = () => {
    navigate('/admin/edge-devices');
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="w-10 h-10 flex items-center justify-center rounded-lg
                   hover:bg-muted transition-colors
                   text-muted-foreground
                   hover:text-foreground"
          title="Quick Actions"
        >
          <Plus className="w-5 h-5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Quick Actions</DropdownMenuLabel>
        <DropdownMenuSeparator />

        <DropdownMenuItem onClick={handleNewFlow}>
          <FileText className="mr-2 h-4 w-4" />
          <span>New Flow</span>
          <DropdownMenuShortcut>N</DropdownMenuShortcut>
        </DropdownMenuItem>

        <DropdownMenuItem onClick={handleRunFlow}>
          <Play className="mr-2 h-4 w-4" />
          <span>Run Flow</span>
          <DropdownMenuShortcut>R</DropdownMenuShortcut>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem onClick={handleNewSchedule}>
          <Calendar className="mr-2 h-4 w-4" />
          <span>New Schedule</span>
          <DropdownMenuShortcut>S</DropdownMenuShortcut>
        </DropdownMenuItem>

        {isSystemAdmin && (
          <DropdownMenuItem onClick={handleViewDevices}>
            <Monitor className="mr-2 h-4 w-4" />
            <span>View Devices</span>
            <DropdownMenuShortcut>D</DropdownMenuShortcut>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
